import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Button, Icon, Input, Skeleton, Tag, Tooltip, message } from 'antd';
import styled from 'styled-components';

import { request } from '~/_shared/instance';
import { department as departmentName } from '~/components/RegisterTasks/Utils';
import {
  REGIMES, ACTIVITIES, PAYROLL,
  getProfile, suggestProfile, suggestActivity, suggestPayroll, describeRule, hasRule,
  choiceFromProfile, profileKeyFromChoice, missingInChoice, choiceLocks, normalizeChoice, emptyChoice,
  tasksForProfile, TASK_HAS, TASK_MISSING, TASK_EXTRA,
  groupChildren, groupApplies, groupStatus, GROUP_PENDING, GROUP_OVER, GROUP_EXTRA,
} from './enrollment';
import {
  say, Flow, Block, BlockHead, Step, Title, Connector, Joint, Sentence, Tiles, Tile, Footer,
  PAGE, Panel, PanelHead, Row, Who, EmptyPanel, ChoiceBlock, Joiner,
} from './ruleUi';
import ManualTasks from './ManualTasks';
import GroupRow from './GroupRow';

// ---------------------------------------------------------------------------
// Aba "Regra" do cliente — no cadastro e no ticket societário.
//
// O mesmo desenho da regra da tarefa, ao contrário: aqui se diz que tipo de
// cliente é este (regime, atividade, folha) e o resultado mostra quais
// tarefas com regra ele recebe. O sistema sugere pelo cadastro e pela tarefa
// FOLHA DE PAGAMENTO; quem grava é a pessoa.
// ---------------------------------------------------------------------------

const Suggestion = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #91d5ff;
  background: #e6f7ff;
  font-size: 13px;
  color: #0050b3;

  small {
    display: block;
    font-size: 12px;
    color: #597ea8;
  }
`;

const Stage = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: ${(p) => (p.first ? '0' : '28px')} 0 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;

  b {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #1890ff;
    white-space: nowrap;
  }
  span {
    font-size: 15px;
    font-weight: 600;
    color: #262626;
  }
  small {
    display: block;
    font-size: 12px;
    font-weight: 400;
    color: #8c8c8c;
  }
`;

const SaveBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  background: ${(p) => (p.dirty ? '#fffbe6' : '#f6ffed')};
  border: 1px solid ${(p) => (p.dirty ? '#ffe58f' : '#b7eb8f')};
  font-size: 13px;
`;

const STATUS = {};
STATUS[TASK_HAS] = { label: 'Já está', color: 'green' };
STATUS[TASK_MISSING] = { label: 'Falta matricular', color: 'orange' };
STATUS[TASK_EXTRA] = { label: 'A regra não sugere', color: 'red' };

const TITLES = { all: 'Tarefas com regra' };
TITLES[TASK_HAS] = 'Sugeridas e já ligadas';
TITLES[TASK_MISSING] = 'Sugeridas, falta matricular';
TITLES[TASK_EXTRA] = 'Ligadas, mas a regra não sugere';

const HELP = { all: 'O que a regra deste cliente sugere em cada tarefa com regra.' };
HELP[TASK_HAS] = 'A regra sugere e o cliente já está na tarefa.';
HELP[TASK_MISSING] = 'A regra sugere, mas o cliente ainda não está na tarefa. Confira e matricule.';
HELP[TASK_EXTRA] = 'O cliente está na tarefa, mas a regra dele não a sugere. Pode ser exceção combinada — confira antes de remover.';

const EMPTY = {};
EMPTY[TASK_MISSING] = 'O cliente já está em todas as tarefas que a regra sugere.';
EMPTY[TASK_EXTRA] = 'Nenhuma tarefa fora da regra.';
EMPTY[TASK_HAS] = 'Nenhuma tarefa com regra para este cliente ainda.';

// Compara os chips, não a chave: sugestão incompleta (sem CNAE, por exemplo)
// não tem chave, e compararia igual a uma regra vazia — e sumiria.
const sameChoice = (a, b) => {
  const x = normalizeChoice(a);
  const y = normalizeChoice(b);
  return x.regime === y.regime && x.payroll === y.payroll
    && x.activities.slice().sort().join('|') === y.activities.slice().sort().join('|');
};

const describeChoice = (choice) => {
  const c = normalizeChoice(choice);
  if (!c.regime) {
    return null;
  }
  if (c.regime === 'Doméstica') {
    return 'Doméstica';
  }
  const parts = [c.regime];
  if (c.activities.length > 0) {
    parts.push(c.activities.join(' e '));
  }
  if (c.payroll) {
    parts.push(c.payroll.toLowerCase());
  }
  return parts.join(' · ');
};

const CustomerRule = ({ customer, onChange }) => {
  const dispatch = useDispatch();
  const customerId = customer && customer.id;

  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [info, setInfo] = useState(null);
  const [payroll, setPayroll] = useState(null);
  const [savedKey, setSavedKey] = useState(null);
  const [choice, setChoice] = useState(emptyChoice());
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const panelRef = useRef(null);
  const [togglingIds, setTogglingIds] = useState([]);

  const load = useCallback(async () => {
    if (!customerId) {
      return;
    }
    setLoading(true);
    try {
      const response = await request('GET', '/customer/' + customerId + '/tasks', null, false);
      const body = (response && response.data) || {};
      const merged = Object.assign({}, customer, body.customer || {});
      setTasks(body.data || []);
      setGroups(body.groups || []);
      setInfo(merged);
      setPayroll(body.payroll || null);
      setSavedKey(merged.enrollment_profile || null);
      setChoice(merged.enrollment_profile ? choiceFromProfile(merged.enrollment_profile) : emptyChoice());
    } catch (error) {
      // o interceptor da request já avisa
    } finally {
      setLoading(false);
    }
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setSearch('');
    setLimit(PAGE);
    if (filter && panelRef.current && panelRef.current.scrollIntoView) {
      panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [filter]);

  // ── sugestão ─────────────────────────────────────────────────────────────

  const suggestion = useMemo(() => {
    if (!info) {
      return null;
    }
    const regime = info.type_taxation || null;
    const payrollGuess = suggestPayroll(info, payroll);
    const activityGuess = suggestActivity(info);
    const full = suggestProfile(info, payroll);
    const guess = full.key ? choiceFromProfile(full.key) : {
      regime,
      activities: activityGuess.key === 'comercio' ? ['Comércio']
        : activityGuess.key === 'servicos' ? ['Serviços']
          : activityGuess.key === 'ambos' ? ['Comércio', 'Serviços'] : [],
      payroll: payrollGuess.key === 'com-folha' ? 'Com folha' : 'Sem folha',
    };
    return {
      choice: normalizeChoice(guess),
      reasons: full.reasons || [],
      confidence: full.confidence || 'low',
      complete: !!full.key,
    };
  }, [info, payroll]);

  // ── estado da regra ──────────────────────────────────────────────────────

  const key = profileKeyFromChoice(choice);
  const profile = key ? getProfile(key) : null;
  const dirty = key !== savedKey;
  const missing = missingInChoice(choice);
  const locks = choiceLocks(choice.regime);
  const contradicts = info && info.type_taxation && choice.regime && choice.regime !== info.type_taxation;
  const showSuggestion = suggestion && suggestion.choice.regime && !sameChoice(suggestion.choice, choice);

  // Filhas de grupo não entram nos contadores soltos: o grupo responde por
  // elas, numa linha só.
  const groupIds = groups.map((group) => group.id);
  const looseTasks = useMemo(
    () => tasks.filter((task) => !task.group_id || groupIds.indexOf(task.group_id) === -1),
    [tasks, groups] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const result = useMemo(() => tasksForProfile(looseTasks, profile), [looseTasks, profile]);

  const groupRows = useMemo(() => groups.map((group) => {
    const items = groupChildren(group, tasks);
    const applies = groupApplies(group, profile);
    return { group, items, applies, status: groupStatus(group, items, applies) };
  }).filter((row) => row.items.length > 0), [groups, tasks, profile]);

  // Etapa 1: grupos que valem para este cliente (pela regra, ou por exigirem
  // cobertura de todo cliente) e os que a regra não traz mas ele está.
  const ruleGroups = groupRows.filter((row) => row.applies === true || row.status.state === GROUP_EXTRA);
  // Etapa 2: grupos livres e sem regra — escolha à mão, como tarefa sem regra.
  const manualGroups = groupRows.filter((row) => !hasRule(row.group) && !row.group.coverage);
  const pendingGroups = ruleGroups.filter((row) => (
    row.status.state === GROUP_PENDING || row.status.state === GROUP_OVER || row.status.state === GROUP_EXTRA
  )).length;

  const setRegime = (values) => {
    const regime = values[0] || null;
    setChoice(normalizeChoice({ regime, activities: choice.activities, payroll: choice.payroll }));
  };

  // ── ações ────────────────────────────────────────────────────────────────

  const saveRule = async () => {
    setSaving(true);
    try {
      await request('POST', '/customer/' + customerId + '/enrollment-profile', { profile: key }, false);
      setSavedKey(key);
      dispatch({ type: 'SET_CUSTOMER_ENROLLMENT_PROFILE', payload: { id: customerId, profile: key } });
      message.success('Regra do cliente salva.');
      if (onChange) onChange();
    } catch (error) {
      // o interceptor da request já avisa
    } finally {
      setSaving(false);
    }
  };

  const changeTasks = async (method, rows, kind) => {
    const ids = rows.map((row) => row.task.id);
    setBusy(kind);
    try {
      await request(method, '/customer/' + customerId + '/tasks', { task_ids: ids }, false);
      const assigned = method === 'POST';
      setTasks((previous) => previous.map((task) => (
        ids.indexOf(task.id) > -1 ? Object.assign({}, task, { is_assigned: assigned }) : task
      )));
      message.success(assigned
        ? say(ids.length, '1 tarefa matriculada.', ids.length + ' tarefas matriculadas.')
        : say(ids.length, '1 tarefa removida.', ids.length + ' tarefas removidas.'));
      if (onChange) onChange();
    } catch (error) {
      // o interceptor da request já avisa
    } finally {
      setBusy(null);
    }
  };

  // Etapa 2: tarefa sem regra não é sugerida; liga e desliga na hora,
  // uma ou várias (o lote vem do filtro da lista).
  const toggleManual = async (list, on) => {
    if (!list.length) return;
    const ids = list.map((task) => task.id);
    setTogglingIds(ids);
    try {
      await request(on ? 'POST' : 'DELETE', '/customer/' + customerId + '/tasks', { task_ids: ids }, false);
      setTasks((previous) => previous.map((item) => (
        ids.indexOf(item.id) > -1 ? Object.assign({}, item, { is_assigned: on }) : item
      )));
      message.success(list.length === 1
        ? '"' + list[0].name + '" ' + (on ? 'ligada' : 'desligada') + ' para este cliente.'
        : list.length + ' tarefas ' + (on ? 'ligadas.' : 'desligadas.'));
      if (onChange) onChange();
    } catch (error) {
      // o interceptor da request já avisa
    } finally {
      setTogglingIds([]);
    }
  };

  if (!customerId) {
    return <Alert type="info" showIcon message="Salve o cliente para definir a regra dele." />;
  }
  if (loading) {
    return <Skeleton active />;
  }

  const byStatus = (status) => result.rows.filter((row) => row.status === status);
  const term = search.trim().toLowerCase();
  const filtered = result.rows.filter((row) => (
    (filter === 'all' || row.status === filter) &&
    (!term || String(row.task.name || '').toLowerCase().indexOf(term) > -1)
  ));
  const shown = filtered.slice(0, limit);
  const openFilter = (k) => setFilter(filter === k ? null : k);

  const bulk = () => {
    if (filter === TASK_MISSING && result.missing > 0) {
      return (
        <Button size="small" type="primary" icon="plus" loading={busy === 'enroll'} disabled={!!busy && busy !== 'enroll'}
          onClick={() => changeTasks('POST', byStatus(TASK_MISSING), 'enroll')}>
          Matricular {say(result.missing, 'a tarefa', 'as ' + result.missing + ' tarefas')}
        </Button>
      );
    }
    if (filter === TASK_EXTRA && result.extra > 0) {
      return (
        <Button size="small" type="danger" icon="minus" loading={busy === 'remove'} disabled={!!busy && busy !== 'remove'}
          onClick={() => changeTasks('DELETE', byStatus(TASK_EXTRA), 'remove')}>
          Remover {say(result.extra, 'a tarefa', 'as ' + result.extra + ' tarefas')}
        </Button>
      );
    }
    return null;
  };

  const rowAction = (row) => {
    if (row.status === TASK_MISSING) {
      return (
        <Button size="small" type="primary" ghost disabled={!!busy} onClick={() => changeTasks('POST', [row], 'enroll')}>
          Matricular
        </Button>
      );
    }
    if (row.status === TASK_EXTRA) {
      return (
        <Button size="small" type="danger" ghost disabled={!!busy} onClick={() => changeTasks('DELETE', [row], 'remove')}>
          Remover
        </Button>
      );
    }
    return null;
  };

  const manualTasks = looseTasks
    .filter((task) => !hasRule(task) && (!task.disabled_at || task.is_assigned))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));

  const tiles = [
    { key: TASK_HAS, tone: 'green', count: result.has, label: say(result.has, 'sugerida e já ligada', 'sugeridas e já ligadas') },
    { key: TASK_MISSING, tone: 'orange', count: result.missing, label: say(result.missing, 'sugerida, falta matricular', 'sugeridas, falta matricular'), cta: result.missing > 0 ? 'Matricular' : null },
  ];
  if (result.extra > 0 || filter === TASK_EXTRA) {
    tiles.push({ key: TASK_EXTRA, tone: 'red', count: result.extra, label: 'ligada, mas a regra não sugere', cta: result.extra > 0 ? 'Revisar' : null });
  }

  return (
    // O respiro embaixo deixa o fim da lista livre do rodapé fixo da gaveta.
    <Flow data-customer-rule style={{ paddingBottom: 72 }}>
      <Stage first data-stage="1">
        <b>Etapa 1</b>
        <span>
          Regra do cliente
          <small>Diz que tipo de cliente é este. Com ela, o sistema sugere as tarefas que combinam — você confere e matricula.</small>
        </span>
      </Stage>
      {showSuggestion && (
        <Suggestion data-suggestion>
          <Icon type="bulb" style={{ fontSize: 18 }} />
          <div style={{ flex: 1 }}>
            Pelo cadastro, este cliente é <strong>{describeChoice(suggestion.choice)}</strong>
            {suggestion.confidence === 'low' && <Tag style={{ marginLeft: 6 }}>palpite</Tag>}
            <small>{suggestion.reasons.join(' · ')}</small>
          </div>
          <Button size="small" type="primary" icon="thunderbolt" onClick={() => setChoice(suggestion.choice)}>
            Usar sugestão
          </Button>
        </Suggestion>
      )}

      <ChoiceBlock
        axis="regime"
        step="1"
        title="Regime tributário"
        help={info && info.type_taxation ? 'No cadastro: ' + info.type_taxation : 'O cadastro não diz o regime.'}
        options={REGIMES}
        value={choice.regime ? [choice.regime] : []}
        onChange={setRegime}
        single
        separator={null}
      />
      {contradicts && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message={'A regra diz ' + choice.regime + ', mas o cadastro diz ' + info.type_taxation + '. Confira qual está certo.'}
        />
      )}

      <Joiner note={choice.regime === 'Doméstica' ? 'Doméstica não usa atividade nem folha'
        : choice.regime === 'Profissional Liberal' ? 'Profissional Liberal presta serviço' : null} />

      <ChoiceBlock
        axis="activities"
        step="2"
        title="Atividade"
        help="Comércio, serviços ou os dois. Vem dos CNAEs do cadastro."
        options={ACTIVITIES}
        value={choice.activities}
        onChange={(activities) => setChoice(Object.assign({}, choice, { activities }))}
        disabled={!choice.regime || locks.activities}
        separator="e"
      />

      <Joiner />

      <ChoiceBlock
        axis="payroll"
        step="3"
        title="Folha de pagamento"
        help={payroll ? 'Sugerida pela tarefa FOLHA DE PAGAMENTO e pelo cadastro.' : 'Tem funcionário registrado?'}
        options={PAYROLL}
        value={choice.payroll ? [choice.payroll] : []}
        onChange={(values) => setChoice(Object.assign({}, choice, { payroll: values[0] || null }))}
        disabled={!choice.regime || locks.payroll}
        single
        separator={null}
      />

      <Connector>
        <Joint style={{ borderColor: '#b7eb8f', color: '#389e0d' }}>
          <Icon type="arrow-down" />
        </Joint>
      </Connector>

      <Block data-result>
        <BlockHead>
          <Step bg={profile ? '#52c41a' : '#bfbfbf'}><Icon type="unordered-list" /></Step>
          <Title>
            Tarefas sugeridas para este cliente
            <small>Só tarefas com regra — as sem regra ficam na Etapa 2</small>
          </Title>
          {profile && (
            <Button size="small" type="link" onClick={() => openFilter('all')}>
              {filter === 'all' ? 'Fechar lista' : 'Ver todas'}
            </Button>
          )}
        </BlockHead>

        {!profile ? (
          <Sentence style={{ color: '#8c8c8c' }}>
            Falta escolher {missing.join(' e ')} para o sistema sugerir as tarefas.
          </Sentence>
        ) : (
          <>
            <Sentence>Este cliente é <strong>{describeChoice(choice)}</strong>.</Sentence>
            <Tiles>
              {tiles.map((tile) => (
                <Tile key={tile.key} type="button" tone={tile.tone} aria-pressed={filter === tile.key}
                  onClick={() => openFilter(tile.key)} data-reach={tile.key}>
                  <strong>{tile.count}</strong>
                  <span>{tile.label}</span>
                  {tile.cta && <em>{tile.cta} <Icon type={filter === tile.key ? 'up' : 'down'} /></em>}
                </Tile>
              ))}
            </Tiles>
            {result.manual > 0 && !filter && (
              <Footer>{say(result.manual, '1 tarefa sem regra fica', result.manual + ' tarefas sem regra ficam')} na Etapa 2, logo abaixo.</Footer>
            )}

            {filter && (
              <Panel ref={panelRef} data-reach-panel>
                <PanelHead>
                  <div style={{ flex: 1 }}>
                    <h4>{TITLES[filter]} · {filtered.length}</h4>
                    <p>{HELP[filter]}</p>
                  </div>
                  {bulk()}
                  <Button size="small" icon="close" type="link" style={{ color: '#8c8c8c' }} onClick={() => setFilter(null)} aria-label="Fechar lista" />
                </PanelHead>
                {result.rows.length > PAGE && (
                  <div style={{ padding: '8px 12px 0' }}>
                    <Input size="small" allowClear prefix={<Icon type="search" style={{ color: '#bfbfbf' }} />}
                      placeholder="Buscar tarefa" value={search} onChange={(event) => setSearch(event.target.value)} />
                  </div>
                )}
                {shown.length === 0 ? (
                  <EmptyPanel>
                    <Icon type="check-circle" style={{ color: '#52c41a', marginRight: 6 }} />
                    {term ? 'Nenhuma tarefa com esse nome.' : (EMPTY[filter] || 'Nenhuma tarefa nesta situação.')}
                  </EmptyPanel>
                ) : shown.map((row) => {
                  const status = STATUS[row.status];
                  return (
                    <Row key={row.task.id}>
                      <Who>
                        <div><strong>{row.task.name}</strong></div>
                        <Tooltip title={'Regra da tarefa: ' + describeRule(row.task)}>
                          <small>{departmentName(row.task.department) || 'Sem departamento'} · regra: {describeRule(row.task)}</small>
                        </Tooltip>
                      </Who>
                      {filter === 'all' && <Tag color={status.color}>{status.label}</Tag>}
                      {rowAction(row)}
                    </Row>
                  );
                })}
                {filtered.length > shown.length && (
                  <div style={{ padding: 8, textAlign: 'center', borderTop: '1px solid #f5f5f5' }}>
                    <Button size="small" type="link" onClick={() => setLimit(limit + PAGE * 2)}>
                      Mostrar mais ({filtered.length - shown.length})
                    </Button>
                  </div>
                )}
              </Panel>
            )}
          </>
        )}
      </Block>

      {ruleGroups.length > 0 && (
        <Block data-rule-groups style={{ marginTop: 14 }}>
          <BlockHead>
            <Step bg={pendingGroups > 0 ? '#fa8c16' : '#52c41a'}><Icon type="folder" /></Step>
            <Title>
              Grupos de tarefas
              <small>
                {pendingGroups > 0
                  ? say(pendingGroups, '1 grupo pede atenção', pendingGroups + ' grupos pedem atenção') + ' — abra e escolha as tarefas.'
                  : 'Cada grupo diz em quantas tarefas o cliente precisa estar.'}
              </small>
            </Title>
          </BlockHead>
          <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
            {ruleGroups.map((row) => (
              <GroupRow
                key={row.group.id}
                group={row.group}
                items={row.items}
                status={row.status}
                onToggle={toggleManual}
                busyIds={togglingIds}
                defaultOpen={row.status.state === GROUP_PENDING || row.status.state === GROUP_OVER}
              />
            ))}
          </div>
        </Block>
      )}

      {/* Sem regra é pendência (todo cliente precisa de uma): a barra fica em alerta. */}
      <SaveBar dirty={dirty || !savedKey} data-save-bar>
        <span>
          {dirty
            ? (key ? 'A regra mudou e ainda não foi salva.' : 'Complete a regra para poder salvar.')
            : (savedKey ? 'Regra salva.' : 'Este cliente ainda não tem regra.')}
        </span>
        <Button type="primary" icon="save" disabled={!dirty || !key} loading={saving} onClick={saveRule}>
          Salvar regra
        </Button>
      </SaveBar>

      <Stage data-stage="2">
        <b>Etapa 2</b>
        <span>
          Tarefas sem regra
          <small>Sem regra ou com regra ainda a definir, o sistema não as sugere: revise uma a uma e ligue as que este cliente recebe. Grupos aparecem juntos, numa linha.</small>
        </span>
      </Stage>
      <ManualTasks tasks={manualTasks} groups={manualGroups} onToggle={toggleManual} busyIds={togglingIds} />
    </Flow>
  );
};

export default CustomerRule;
